/**
 * Datos de artículos del blog
 * Centralizado para fácil mantenimiento
 */

export const BLOG_POSTS = [
  {
    id: 'sindrome-cuidador-quemado',
    slug: 'sindrome-cuidador-quemado',
    title: 'Síndrome del Cuidador Quemado: Cómo Reconocerlo y Cuidarte Mientras Cuidas',
    excerpt: 'El agotamiento físico y emocional de cuidar a un familiar mayor es más común de lo que parece. Aprende a identificar las señales de burnout del cuidador y qué hacer al respecto.',
    content: `
      <h2>Un problema que casi ninguna familia habla en voz alta</h2>
      <p>Cuidar de un padre, madre o abuelo es un acto de amor, pero también es un trabajo físico y emocional que rara vez se detiene. Muchas personas cuidadoras siguen una rutina así, día tras día, durante meses o incluso años: despertar antes del amanecer para dar medicamentos, ayudar a bañar y vestir, preparar alimentos especiales, acompañar a citas médicas, estar alerta toda la noche por si hay una caída. Con el tiempo, esta carga constante puede pasar factura al propio cuidador, un fenómeno conocido como <strong>síndrome del cuidador quemado</strong> (burnout del cuidador).</p>
      <p>No es falta de amor ni de voluntad. Es agotamiento acumulado, y reconocerlo a tiempo es el primer paso para evitar que afecte tanto al cuidador como a la persona que recibe los cuidados.</p>

      <h2>¿Qué es el síndrome del cuidador quemado?</h2>
      <p>Se trata de un estado de agotamiento físico, emocional y mental que aparece cuando una persona dedica una parte muy grande de su tiempo y energía al cuidado de un familiar, generalmente sin suficiente descanso, apoyo o tiempo para sí misma. Es especialmente frecuente en cuidadores de adultos mayores con enfermedades crónicas, demencia, Alzheimer o dependencia total para las actividades diarias.</p>

      <h2>Señales de alarma que no debes ignorar</h2>
      <p>El burnout del cuidador no aparece de un día para otro; se va acumulando. Algunas señales comunes son:</p>
      <ul>
        <li>Cansancio constante que no mejora con dormir</li>
        <li>Irritabilidad o cambios de humor frecuentes</li>
        <li>Sensación de estar "siempre en alerta" y no poder relajarte</li>
        <li>Aislamiento: dejar de ver amigos, salir o dedicar tiempo a ti mismo</li>
        <li>Sentimientos de culpa por necesitar descanso o ayuda</li>
        <li>Dificultad para dormir o descansar aun cuando hay oportunidad</li>
        <li>Dolores de cabeza, tensión muscular o problemas digestivos frecuentes</li>
        <li>Sensación de que "nadie más lo hace bien" y dificultad para delegar</li>
        <li>Descuido de tu propia alimentación, ejercicio o citas médicas</li>
      </ul>
      <p>Si te identificas con varias de estas señales, no significa que estés fallando como cuidador: significa que tu cuerpo y tu mente están pidiendo apoyo.</p>

      <h2>¿Por qué ocurre con tanta frecuencia?</h2>
      <p>En la mayoría de las familias mexicanas, el cuidado de un adulto mayor recae en una sola persona: casi siempre una hija, esposa o nuera, que además suele tener trabajo, hijos y su propia casa que atender. La falta de una red de apoyo estructurada, la dificultad para pedir ayuda y la idea de que "cuidar a la familia es obligación de uno solo" hacen que el desgaste se acumule en silencio durante mucho tiempo antes de que se note.</p>

      <h2>Estrategias prácticas para cuidarte mientras cuidas</h2>
      <h3>1. Acepta que pedir ayuda no es debilidad</h3>
      <p>Involucrar a otros familiares, aunque sea en tareas puntuales, o contratar apoyo profesional para algunas horas o días a la semana, no te hace "menos buen hijo o hija". Te permite sostener el cuidado a largo plazo sin colapsar.</p>

      <h3>2. Protege bloques de tiempo para ti</h3>
      <p>Aunque sean 30 minutos al día, reserva tiempo para caminar, ver a un amigo, leer o simplemente no hacer nada. El descanso no es un lujo, es lo que te permite seguir cuidando con calidad.</p>

      <h3>3. Cuida tu cuerpo</h3>
      <p>Duerme lo suficiente cuando sea posible, mantén una alimentación balanceada y no pospongas tus propias consultas médicas. Un cuidador enfermo no puede cuidar bien a nadie.</p>

      <h3>4. Considera el "cuidado de relevo" (respiro para el cuidador)</h3>
      <p>Contar con una enfermera o cuidadora profesional que cubra algunos turnos, días o solo las noches, te permite recuperar horas de sueño y espacio mental sin dejar de estar presente en el cuidado general de tu familiar. Muchas familias esperan hasta llegar al límite para buscar este tipo de apoyo, cuando en realidad puede solicitarse desde antes, de forma preventiva.</p>

      <h3>5. Habla de lo que sientes</h3>
      <p>Compartir con otras personas que están viviendo lo mismo, o buscar apoyo psicológico, ayuda a procesar emociones como la culpa, la frustración o la tristeza que son completamente normales en este proceso.</p>

      <h2>¿Cuándo buscar ayuda profesional?</h2>
      <p>Es momento de buscar apoyo externo cuando el cansancio se vuelve constante, cuando aparecen síntomas de ansiedad o tristeza persistente, o cuando sientes que ya no puedes sostener el ritmo actual sin afectar tu salud. Contar con personal de enfermería certificado a domicilio, aunque sea por unas horas al día o algunos turnos a la semana, puede marcar la diferencia entre llegar al agotamiento total o sostener el cuidado de tu familiar de forma saludable y sostenible en el tiempo.</p>
      <p>En Geriatría Serena entendemos que cuidar a quien cuida es parte esencial del proceso. Podemos apoyarte con turnos de relevo, cuidado nocturno o enfermería de tiempo completo, para que tu familiar esté en buenas manos mientras tú también descansas.</p>
    `,
    keyPoints: [
      'El síndrome del cuidador quemado es agotamiento físico, emocional y mental por cuidar sin descanso ni apoyo.',
      'Señales de alarma: cansancio constante, irritabilidad, aislamiento, culpa por descansar e insomnio.',
      'Es más frecuente cuando el cuidado recae en una sola persona sin red de apoyo.',
      'Pedir ayuda, proteger tiempo propio y cuidar tu cuerpo son estrategias clave para sostener el cuidado.',
      'El cuidado de relevo con enfermería profesional permite descansar sin dejar de cuidar a tu familiar.'
    ],
    relatedService: { path: '/enfermeria-24-horas', label: 'Enfermería 24 Horas' },
    author: 'Equipo Geriatría Serena',
    date: '2026-07-30',
    readTime: '6 min',
    category: 'Bienestar del Cuidador',
    image: 'cuidadoras',
    tags: ['síndrome del cuidador', 'burnout', 'autocuidado', 'salud mental', 'cuidado de relevo']
  },
  {
    id: 'cuidados-postoperatorios-casa',
    slug: 'cuidados-postoperatorios-casa',
    title: 'Cuidados Postoperatorios en Casa: Guía Completa 2026',
    excerpt: 'Todo lo que necesitas saber para cuidar a un familiar después de una cirugía. Consejos de enfermeras profesionales.',
    content: `
      <h2>¿Por qué son importantes los cuidados postoperatorios?</h2>
      <p>Después de una cirugía, el cuerpo necesita tiempo y cuidados especiales para recuperarse completamente. Los primeros días son cruciales para prevenir complicaciones como infecciones, trombosis o problemas con la herida quirúrgica.</p>
      
      <h2>Cuidados básicos de la herida</h2>
      <p>La herida quirúrgica requiere atención especial: mantenga la zona limpia y seca, cambie el apósito según las indicaciones médicas y no la moje a menos que el médico lo autorice. Vigile en todo momento signos de infección como enrojecimiento, hinchazón, secreción o mal olor.</p>

      <h2>Administración de medicamentos</h2>
      <p>Es fundamental seguir el horario de medicamentos al pie de la letra: los analgésicos se administran antes de que el dolor se vuelva intenso, los antibióticos deben completarse aunque el paciente se sienta mejor, y los anticoagulantes se aplican siempre a la misma hora cada día.</p>

      <h2>Signos de alarma</h2>
      <p>Busque atención médica inmediata si observa:</p>
      <ul>
        <li>Fiebre mayor a 38°C</li>
        <li>Sangrado excesivo</li>
        <li>Dolor que no cede con medicamento</li>
        <li>Dificultad para respirar</li>
        <li>Hinchazón o enrojecimiento en piernas</li>
      </ul>
      
      <h2>¿Cuándo considerar enfermería a domicilio?</h2>
      <p>Una enfermera profesional es recomendable cuando:</p>
      <ul>
        <li>La cirugía fue mayor (abdominal, cardiaca, ortopédica)</li>
        <li>El paciente vive solo o con familiar de edad avanzada</li>
        <li>Hay comorbilidades como diabetes o hipertensión</li>
        <li>Se requieren curaciones especializadas o drenajes</li>
      </ul>
    `,
    keyPoints: [
      'Los primeros días tras la cirugía son críticos para prevenir infecciones y complicaciones.',
      'La herida quirúrgica debe mantenerse limpia, seca y vigilada según indicaciones médicas.',
      'Los medicamentos (analgésicos, antibióticos, anticoagulantes) deben administrarse en el horario exacto.',
      'Fiebre, sangrado excesivo, dolor intenso o dificultad para respirar son signos de alarma que requieren atención inmediata.',
      'La enfermería a domicilio es recomendable en cirugías mayores o cuando el paciente vive solo.'
    ],
    relatedService: { path: '/atencion-postoperatoria', label: 'Atención Postoperatoria' },
    author: 'Equipo Geriatría Serena',
    date: '2026-01-08',
    readTime: '5 min',
    category: 'Cuidados',
    image: 'auxiliar',
    tags: ['postoperatorio', 'cirugía', 'cuidados', 'recuperación']
  },
  {
    id: 'signos-alarma-adultos-mayores',
    slug: 'signos-alarma-adultos-mayores',
    title: 'Signos de Alarma en Adultos Mayores que No Debes Ignorar',
    excerpt: 'Conoce los síntomas que requieren atención médica inmediata en personas de la tercera edad.',
    content: `
      <h2>La importancia de estar alerta</h2>
      <p>Los adultos mayores pueden tener síntomas atípicos de enfermedades graves. Conocer los signos de alarma puede salvar vidas.</p>
      
      <h2>Señales de alarma por categoría</h2>
      <p>Estas son las señales más importantes a vigilar, agrupadas por tipo:</p>
      <table>
        <thead>
          <tr>
            <th>Categoría</th>
            <th>Qué vigilar</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Estado mental</td>
            <td>Confusión repentina o desorientación, somnolencia excesiva o dificultad para despertar, cambios bruscos de personalidad, agitación inexplicable. Puede indicar infección, deshidratación, efectos de medicamentos o una emergencia neurológica.</td>
          </tr>
          <tr>
            <td>Respiración</td>
            <td>Dificultad para respirar en reposo, respiración rápida y superficial, coloración azulada en labios o uñas, tos persistente con flema.</td>
          </tr>
          <tr>
            <td>Corazón y circulación</td>
            <td>Dolor en el pecho (puede ser leve en adultos mayores), palpitaciones irregulares, hinchazón en piernas y pies, mareos frecuentes.</td>
          </tr>
          <tr>
            <td>Caídas y traumatismos</td>
            <td>Golpe en la cabeza, dolor intenso en cadera o extremidades, no poder levantarse o caminar, moretones sin causa aparente.</td>
          </tr>
        </tbody>
      </table>

      <h2>¿Qué hacer ante estos signos?</h2>
      <p>Ante cualquier signo de alarma:</p>
      <ol>
        <li>Mantenga la calma</li>
        <li>Llame a emergencias o al médico tratante</li>
        <li>No administre medicamentos sin indicación</li>
        <li>Prepare información médica del paciente</li>
      </ol>
    `,
    keyPoints: [
      'Los adultos mayores pueden presentar síntomas atípicos ante enfermedades graves.',
      'Confusión repentina, somnolencia excesiva o cambios de personalidad pueden indicar una urgencia.',
      'Dificultad para respirar, coloración azulada o tos persistente requieren evaluación médica.',
      'Dolor en el pecho, palpitaciones o hinchazón en piernas son señales cardiovasculares de alerta.',
      'Ante cualquier signo de alarma, mantenga la calma y contacte de inmediato a emergencias o al médico tratante.'
    ],
    relatedService: { path: '/cuidados-geriatricos', label: 'Cuidados Geriátricos' },
    author: 'Equipo Geriatría Serena',
    date: '2026-01-06',
    readTime: '4 min',
    category: 'Salud',
    image: 'cuidadoras',
    tags: ['signos alarma', 'adultos mayores', 'emergencias', 'salud']
  },
  {
    id: 'enfermeria-domicilio-guadalajara-2026',
    slug: 'enfermeria-domicilio-guadalajara-2026',
    title: 'Enfermería a Domicilio en Guadalajara 2026: Guía Completa',
    excerpt: 'Todo sobre servicios de enfermería domiciliaria en Guadalajara: costos, tipos de servicio y cómo elegir el mejor.',
    content: `
      <h2>El crecimiento de la enfermería domiciliaria</h2>
      <p>La demanda de servicios de enfermería a domicilio en Guadalajara ha crecido significativamente. Cada vez más familias prefieren que sus seres queridos reciban atención en la comodidad de su hogar.</p>
      
      <h2>Tipos de servicios disponibles</h2>
      <h3>Cuidadoras</h3>
      <p>Personal capacitado para asistir en actividades diarias, compañía y apoyo básico.</p>
      
      <h3>Auxiliares de Enfermería</h3>
      <p>Profesionales que pueden realizar procedimientos básicos, curaciones menores y administrar medicamentos orales.</p>
      
      <h3>Enfermeras Generales</h3>
      <p>Profesionales tituladas para procedimientos especializados, medicamentos inyectables y cuidados postoperatorios.</p>
      
      <h2>¿Cuánto cuesta la enfermería a domicilio?</h2>
      <p>Los costos varían según:</p>
      <ul>
        <li>Tipo de profesional requerido</li>
        <li>Duración del servicio (horas o turno completo)</li>
        <li>Complejidad de los cuidados</li>
        <li>Horario (diurno o nocturno)</li>
      </ul>
      <p>En promedio, los servicios van desde $800 hasta $2,000 MXN por turno de 8 horas.</p>
      
      <h2>Cómo elegir un servicio de enfermería</h2>
      <ol>
        <li>Verificar que el personal esté certificado</li>
        <li>Solicitar referencias o testimonios</li>
        <li>Confirmar disponibilidad y flexibilidad</li>
        <li>Preguntar sobre supervisión y protocolos</li>
        <li>Conocer las políticas de cancelación</li>
      </ol>
      
      <h2>Ventajas de la atención domiciliaria</h2>
      <ul>
        <li>Comodidad del ambiente familiar</li>
        <li>Atención personalizada uno a uno</li>
        <li>Menor riesgo de infecciones hospitalarias</li>
        <li>Menor costo que hospitalización</li>
        <li>Participación activa de la familia</li>
      </ul>
    `,
    keyPoints: [
      'La demanda de enfermería a domicilio en Guadalajara ha crecido por la preferencia de atender en casa.',
      'Existen tres niveles de personal: cuidadoras, auxiliares de enfermería y enfermeras generales tituladas.',
      'El costo promedio va de $800 a $2,000 MXN por turno de 8 horas, según el tipo de servicio.',
      'Verificar certificaciones, referencias y protocolos de supervisión es clave antes de contratar.',
      'La atención domiciliaria reduce riesgos de infección hospitalaria y cuesta menos que una hospitalización.'
    ],
    relatedService: { path: '/services', label: 'Catálogo de Servicios' },
    author: 'Equipo Geriatría Serena',
    date: '2026-01-10',
    readTime: '6 min',
    category: 'Guías',
    image: 'general',
    tags: ['enfermería domicilio', 'Guadalajara', 'guía', 'costos']
  },
  {
    id: 'cuidados-geriatricos-zapopan',
    slug: 'cuidados-geriatricos-zapopan',
    title: 'Cuidados Geriátricos en Zapopan: Opciones y Recomendaciones',
    excerpt: 'Descubre las mejores opciones de cuidado para adultos mayores en Zapopan y cómo elegir la adecuada.',
    content: `
      <h2>El envejecimiento en Zapopan</h2>
      <p>Zapopan tiene una población significativa de adultos mayores que requieren diversos niveles de atención. Conocer las opciones disponibles es fundamental para tomar la mejor decisión.</p>
      
      <h2>Opciones de cuidado disponibles</h2>
      <p>Antes de decidir, conviene comparar las tres opciones más comunes y para qué perfil de paciente es ideal cada una:</p>
      <table>
        <thead>
          <tr>
            <th>Opción</th>
            <th>Ideal cuando...</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Cuidado en casa</td>
            <td>Es la opción preferida por la mayoría de familias: permite mantener rutinas y ambiente familiar, ofrece atención personalizada, flexibilidad de horarios y participación activa de la familia.</td>
          </tr>
          <tr>
            <td>Centros de día</td>
            <td>El adulto mayor puede trasladarse, busca socialización y requiere supervisión solo durante el día.</td>
          </tr>
          <tr>
            <td>Residencias geriátricas</td>
            <td>Se requiere atención médica 24/7, la familia no puede brindar los cuidados necesarios o hay deterioro cognitivo avanzado.</td>
          </tr>
        </tbody>
      </table>

      <h2>Zonas de Zapopan con más servicios</h2>
      <p>La mayor oferta de servicios de cuidado geriátrico se concentra en Providencia y Country Club, Chapalita y Jardines del Bosque, Puerta de Hierro, y Santa Margarita.</p>

      <h2>Recomendaciones para elegir</h2>
      <ol>
        <li>Evalúe las necesidades específicas del adulto mayor</li>
        <li>Considere las preferencias del paciente</li>
        <li>Visite las instalaciones o conozca al personal</li>
        <li>Verifique referencias y certificaciones</li>
        <li>Compare costos y servicios incluidos</li>
      </ol>
    `,
    keyPoints: [
      'Zapopan tiene una población grande de adultos mayores con distintas necesidades de cuidado.',
      'Las opciones principales son cuidado en casa, centros de día y residencias geriátricas.',
      'El cuidado en casa ofrece mayor personalización, flexibilidad y participación familiar.',
      'Providencia, Chapalita, Puerta de Hierro y Santa Margarita concentran más oferta de servicios.',
      'Evaluar necesidades, revisar certificaciones y comparar costos ayuda a elegir la mejor opción.'
    ],
    relatedService: { path: '/enfermeria-zapopan', label: 'Enfermería en Zapopan' },
    author: 'Equipo Geriatría Serena',
    date: '2026-01-05',
    readTime: '5 min',
    category: 'Guías',
    image: 'cuidadoras',
    tags: ['Zapopan', 'cuidados geriátricos', 'adultos mayores', 'opciones']
  },
  {
    id: 'como-elegir-enfermera-domicilio',
    slug: 'como-elegir-enfermera-domicilio',
    title: 'Cómo Elegir la Mejor Enfermera a Domicilio: 10 Consejos Clave',
    excerpt: 'Guía práctica para seleccionar al profesional de enfermería ideal para tu familiar.',
    content: `
      <h2>La importancia de elegir bien</h2>
      <p>Confiar el cuidado de un ser querido a un profesional de enfermería es una decisión importante. Estos consejos te ayudarán a tomar la mejor elección.</p>
      
      <h2>10 Consejos para elegir enfermera a domicilio</h2>
      
      <h3>1. Verifica las credenciales</h3>
      <p>Solicita ver la cédula profesional y certificaciones. Una enfermera general debe tener título y cédula de la SEP.</p>
      
      <h3>2. Evalúa la experiencia</h3>
      <p>Pregunta sobre experiencia específica con condiciones similares a las de tu familiar.</p>
      
      <h3>3. Solicita referencias</h3>
      <p>Pide contactos de familias anteriores que puedan dar testimonio del servicio.</p>
      
      <h3>4. Haz una entrevista personal</h3>
      <p>Conoce a la enfermera antes de contratarla. Evalúa su comunicación y actitud.</p>
      
      <h3>5. Clarifica los servicios incluidos</h3>
      <p>Define exactamente qué tareas realizará y cuáles están fuera de su alcance.</p>
      
      <h3>6. Establece protocolos de comunicación</h3>
      <p>Acuerda cómo y cuándo recibirás reportes del estado del paciente.</p>
      
      <h3>7. Pregunta sobre emergencias</h3>
      <p>Conoce qué haría ante una emergencia y si tiene capacitación en RCP.</p>
      
      <h3>8. Verifica disponibilidad</h3>
      <p>Confirma que pueda cubrir los horarios que necesitas de manera consistente.</p>
      
      <h3>9. Discute la supervisión</h3>
      <p>Si contratas a través de una agencia, pregunta cómo supervisan al personal.</p>
      
      <h3>10. Confía en tu instinto</h3>
      <p>La química entre el cuidador y el paciente es importante. Si algo no se siente bien, busca otra opción.</p>
      
      <h2>Señales de alerta</h2>
      <ul>
        <li>No quiere mostrar documentos</li>
        <li>Evita dar referencias</li>
        <li>Pide pagos en efectivo sin recibo</li>
        <li>No tiene seguro o respaldo de agencia</li>
        <li>Muestra impaciencia o falta de empatía</li>
      </ul>
    `,
    keyPoints: [
      'Verificar cédula profesional y certificaciones es el primer paso antes de contratar.',
      'Pedir referencias y hacer una entrevista personal permite evaluar experiencia y trato.',
      'Es importante clarificar por escrito qué servicios incluye y cómo se manejarán las emergencias.',
      'Confirmar disponibilidad de horarios y protocolos de supervisión evita malentendidos.',
      'Evita personal que no muestre documentos, evite dar referencias o no tenga respaldo de una agencia.'
    ],
    relatedService: { path: '/services', label: 'Catálogo de Servicios' },
    author: 'Equipo Geriatría Serena',
    date: '2026-01-03',
    readTime: '7 min',
    category: 'Consejos',
    image: 'general',
    tags: ['elegir enfermera', 'consejos', 'contratación', 'domicilio']
  }
];

export const BLOG_CATEGORIES = [
  { id: 'todos', name: 'Todos' },
  { id: 'Cuidados', name: 'Cuidados' },
  { id: 'Salud', name: 'Salud' },
  { id: 'Guías', name: 'Guías' },
  { id: 'Consejos', name: 'Consejos' },
  { id: 'Bienestar del Cuidador', name: 'Bienestar del Cuidador' }
];
